import { Component, forwardRef, input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * TimePickerComponent - Handles time input with HTML5 time input
 *
 * This component provides a styled time input that integrates with Angular reactive forms.
 * Uses the native HTML5 time input format (HH:mm) in 24-hour format.
 *
 * Features:
 * - Works with reactive forms via ControlValueAccessor
 * - Supports min/max time constraints
 * - Consistent styling with date-picker and input components
 * - Handles disabled state from form control
 */
@Component({
  selector: 'app-time-picker',
  standalone: true,
  imports: [],
  templateUrl: './time-picker.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimePickerComponent),
      multi: true,
    },
  ],
})
export class TimePickerComponent implements ControlValueAccessor {
  label = input<string>('');
  max = input<string>();
  min = input<string>();

  // Internal value stored in HTML5 time format (HH:mm)
  _value = '';
  isDisabled = false;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange: (value: string) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched: () => void = () => {};

  /**
   * Called by Angular forms when the value is set from the form
   * Expects time in HH:mm format (24-hour)
   */
  writeValue(value: string): void {
    this._value = value || '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  /**
   * Called when the user changes the time in the input
   * Passes the time value in HH:mm format to the form control
   */
  onTimeChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this._value = input.value;
    this.onChange(this._value);
    this.onTouched();
  }
}
